import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, ElementRef, input, output, signal, ViewChild } from '@angular/core';
import { DsyInput } from '@dsy/enelx';
import { FilterEntry, FilterOptions, FilterValues } from '../../../models/filters.model';
import { SearchableDropdownComponent } from '../../../shared/components/searchable-dropdown/searchable-dropdown.component';

@Component({
  selector: 'dr-ops-stream-ui-shift-filters',
  templateUrl: './shift-filters.component.html',
  standalone: true,
  imports: [SearchableDropdownComponent],
  styleUrls: ['./shift-filters.component.scss', '../../../shared/styles/filters.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ShiftFiltersComponent {
  @ViewChild('dueDateInput', { read: ElementRef }) dueDateInput!: ElementRef<DsyInput>;
  readonly options = input.required<FilterOptions>();
  readonly filtersChange = output<FilterValues>();

  readonly activitySignal = signal<string | null>(null);
  readonly programSignal = signal<string | null>(null);
  readonly createdBySignal = signal<string | null>(null);
  readonly dueDateSignal = signal<Date | null>(null);

  private hasAppliedFilters: boolean = false;

  readonly currentFiltersSignal = computed<FilterValues>(() => ({
    activity: this.activitySignal(),
    program: this.programSignal(),
    createdBy: this.createdBySignal(),
    dueDate: this.dueDateSignal(),
    status: null,
  }));

  onActivityChange = (value: string): void => this.activitySignal.set(value);
  onProgramChange = (value: string): void => this.programSignal.set(value);
  onCreatedByChange = (value: string): void => this.createdBySignal.set(value);
  onDueDateChange = (value: string): void => this.dueDateSignal.set(value ? new Date(value) : null);

  readonly applyButtonDisabledSignal = computed(
    () => !this.currentFiltersSignal().activity && !this.currentFiltersSignal().program && !this.currentFiltersSignal().createdBy && !this.currentFiltersSignal().dueDate
  );

  // filter entries for activity, program, and createdBy
  readonly activityFilterEntriesSignal = computed<FilterEntry[]>(() =>
    this.createFilterEntries(this.options().activities, this.activitySignal(), (v) => this.activitySignal.set(v))
  );
  readonly programFilterEntriesSignal = computed<FilterEntry[]>(() => this.createFilterEntries(this.options().programs, this.programSignal(), (v) => this.programSignal.set(v)));
  readonly createdByFilterEntriesSignal = computed<FilterEntry[]>(() =>
    this.createFilterEntries(this.options().creators, this.createdBySignal(), (v) => this.createdBySignal.set(v))
  );

  private createFilterEntries(labels: string[], currentValue: string | null, setter: (value: string) => void): FilterEntry[] {
    return labels.map((label, i) => ({
      id: i + 1,
      labelContent: label,
      isSelected: currentValue === label,
    }));
  }

  onReset(): void {
    this.activitySignal.set(null);
    this.programSignal.set(null);
    this.createdBySignal.set(null);
    this.dueDateSignal.set(null);
    this.clearDate();
    if (this.hasAppliedFilters) {
      this.filtersChange.emit(this.currentFiltersSignal());
      this.hasAppliedFilters = false;
    }
  }

  private clearDate(): void {
    if (!this.dueDateInput) return;

    const inputField = this.dueDateInput.nativeElement.inputField?.value;
    if (inputField instanceof HTMLInputElement && inputField.value) {
      inputField.value = '';
    }
  }

  onApply(): void {
    this.filtersChange.emit(this.currentFiltersSignal());
    this.hasAppliedFilters = true;
  }
}
